import { z } from "zod";

/**
 * Why an MCP OAuth authorization could not be started or completed.
 *
 * One vocabulary for the whole flow, shared by the backend's refusals and by the
 * surface that renders them, so the two cannot drift into two spellings of one
 * failure. Every code names a CLASS the person connecting the server can act on —
 * "the server does not support this", "start again" — rather than a moment in the
 * flow.
 *
 * Deliberately NOT `MCP_SYNC_ERROR_CODES`. That vocabulary answers "why did a
 * tool sync fail", and it is stored on `McpServer.lastSyncErrorCode`; these are
 * refusals of an interactive authorization that writes no sync outcome at all.
 * One of them does appear in both — a server that needs authorization — and it
 * means different things on each side: there it is the OUTCOME of a sync, here it
 * is never reached, because a flow that is running has already established that.
 *
 * A code carries no remote text. An authorization server's error body is
 * attacker-authored, it reaches logs and alerts, and it can echo the credential
 * just sent to it — the same reason `McpServer` has no error MESSAGE column.
 */
export const MCP_OAUTH_ERROR_CODES = [
  /** The server's URL or its metadata did not resolve to an authorization server. */
  "MCP_OAUTH_DISCOVERY_FAILED",
  /**
   * The authorization server's metadata does not advertise S256 PKCE.
   *
   * REFUSED rather than attempted. The MCP authorization specification makes
   * S256 a MUST for every client, and an absent `code_challenge_methods_supported`
   * is not evidence of support — sending a challenge to a server that ignores it
   * produces a flow with no proof of possession at all, and it succeeds, which is
   * what makes assuming support the dangerous reading.
   */
  "MCP_OAUTH_PKCE_UNSUPPORTED",
  /**
   * No client could be established: the server advertises no registration
   * endpoint, does not accept a client-id metadata document, and no client was
   * pre-registered by an admin.
   */
  "MCP_OAUTH_CLIENT_UNAVAILABLE",
  /** Dynamic client registration was attempted and the server refused it. */
  "MCP_OAUTH_REGISTRATION_FAILED",
  /**
   * A pre-registered client was supplied for an issuer that already has one.
   *
   * A REFUSAL, and deliberately not a silent discard. The client belongs to the
   * issuer rather than to one server, so a second server guarded by the same
   * authorization server reaches a row that already exists — and an admin who
   * deliberately typed a credential must not get a successful-looking connection
   * that ignored it. Replacing the row is ruled out separately: every token
   * already issued is bound to the first `client_id`, and orphaning them shows up
   * weeks later as a refresh failing with `invalid_client`.
   *
   * Nothing is written and nothing is changed when this is raised, and there is
   * no way to remove an established client yet, so the only thing the caller can
   * do is connect without supplying one — which works, because the stored client
   * is used automatically.
   */
  "MCP_OAUTH_CLIENT_ALREADY_ESTABLISHED",
  /** No live flow matches this `state` — unknown, another tenant's, or another user's. */
  "MCP_OAUTH_FLOW_NOT_FOUND",
  "MCP_OAUTH_FLOW_EXPIRED",
  "MCP_OAUTH_FLOW_ALREADY_USED",
  /**
   * RFC 9207: the authorization response named an issuer other than the one the
   * request was sent to, or omitted `iss` when the server said it would send one.
   * A mix-up attack looks exactly like this and nothing else does.
   */
  "MCP_OAUTH_ISSUER_MISMATCH",
  /** The authorization server redirected back with an `error` parameter. */
  "MCP_OAUTH_AUTHORIZATION_DENIED",
  /**
   * The grant is missing a scope the flow requested — the person consenting
   * unticked a permission. Judged by COVERAGE: a reordered or wider grant is the
   * request satisfied, and only a genuine downgrade lands here.
   */
  "MCP_OAUTH_SCOPE_NOT_GRANTED",
  /** The server is registered with an auth mode other than OAUTH, so it has no flow to start. */
  "MCP_OAUTH_SERVER_NOT_OAUTH",
  /** The token endpoint refused the authorization code, or the refresh token. */
  "MCP_OAUTH_TOKEN_EXCHANGE_FAILED",
  /** The credential this flow was for is gone, or is not in a state it can complete. */
  "MCP_OAUTH_CREDENTIAL_UNAVAILABLE",
  /** A flow must be owned by a person; there was no signed-in user. */
  "MCP_OAUTH_ACTOR_REQUIRED",
  /** This deployment cannot run the flow at all — no HTTPS base URL to redirect back to. */
  "MCP_OAUTH_NOT_CONFIGURED"
] as const;

export const McpOAuthErrorCodeSchema = z.enum(MCP_OAUTH_ERROR_CODES);

export type McpOAuthErrorCode = z.infer<typeof McpOAuthErrorCodeSchema>;
